import React from "react";
import * as ReactDOM from "react-dom/client";
import "./landing-shared.entry.jsx";
const SiteNav = globalThis.SiteNav ?? globalThis.window?.SiteNav;
const SiteFooter = globalThis.SiteFooter ?? globalThis.window?.SiteFooter;
// About page
const Icon = {
  arrow: (p) => <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}><path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square"/></svg>,
};

function Hero() {
  return (
    <section className="section section-dark about-hero">
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <span className="about-eyebrow"><span className="eb-dot"></span>ABOUT · PRIVATE ACCESS</span>
        <h1>named.network is a<br/>private access layer<br/><em>for what you run.</em></h1>
        <p className="about-hero-sub">
          Protect local apps, internal tools, and trusted devices without opening public ports.
          The product starts with Private Apps and expands into Secure DNS, Private Network,
          AI &amp; Automation, Controlled Gateway, and Exit Nodes.
        </p>
      </div>
    </section>
  );
}

function Summary() {
  return (
    <section className="section section-light">
      <div className="container">
        <div className="manifesto">
          <div className="manifesto-label">What it is</div>
          <div className="manifesto-body">
            <p>named.network is built around one simple idea: the useful things you run should not have to become public websites just so the right person can reach them.</p>
            <p>The core product path is now real: enroll a device, protect a local or LAN HTTP app behind a named hostname, sign in safely, share access by email, and revoke access cleanly.</p>
            <p>The advanced surfaces are real too, but they are framed honestly. Secure DNS, Private Network, AI &amp; Automation, Controlled Gateway, and Exit Nodes are beta, early-access, or Business-tier capabilities rather than broad default promises.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Reality() {
  const items = [
    {
      num: '01',
      title: 'What exists today',
      body: 'The dashboard is wired to real backend reads and mutations across Protected Apps, Devices, Access, Billing, Secure DNS, Private Network, AI, Gateway, Exit Nodes, and Profile.'
    },
    {
      num: '02',
      title: 'What the product is for',
      body: 'Private app protection comes first. The main promise is access without open ports, not a generic consumer VPN story or a broad anonymity pitch.'
    },
    {
      num: '03',
      title: 'What is already strong',
      body: 'Account security is substantial: magic links, TOTP, recovery codes, trusted browsers, WebAuthn, passkeys, backup-email recovery, and admin-assisted recovery are all implemented.'
    },
    {
      num: '04',
      title: 'What is still in progress',
      body: 'The remaining gap to beta is productization: simpler onboarding, tighter advanced-feature guardrails, stronger supportability, and continued docs/runtime honesty.'
    },
  ];
  return (
    <section className="principles">
      <div className="dark-grid"></div>
      <div className="container">
        <div className="section-h">
          <span className="section-label">Current reality</span>
          <h2 style={{ color: 'var(--text-dark-primary)' }}>A real product, still being tightened.</h2>
          <p>This page is intentionally minimal. It should tell the truth about the product, not wrap it in extra narrative.</p>
        </div>
        <div className="principles-grid">
          {items.map((p, i) => (
            <div key={i} className="principle">
              <div className="p-num">NOTE {p.num}</div>
              <h3>{p.title}</h3>
              <p>{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Boundaries() {
  const cards = [
    {
      tag: 'IS',
      t: 'Private access to your apps, devices, and internal tools.',
      b: 'That is the center of the product and the standard we should hold the rest of the surface against.'
    },
    {
      tag: 'IS',
      t: 'A platform that can grow into advanced DNS, network, automation, and egress controls.',
      b: 'Those capabilities are already present, but they should still be described with the right beta or Business framing.'
    },
    {
      tag: 'IS NOT',
      t: 'A streaming unblocker or anonymity product.',
      b: 'We should not use consumer-VPN language that confuses private access with untraceable public browsing.'
    },
    {
      tag: 'IS NOT',
      t: 'A product that pretends every advanced surface is polished for everyone already.',
      b: 'The honest story is stronger: the product is real, and the advanced areas are being tightened deliberately.'
    },
  ];
  return (
    <section className="honest">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Plainly</span>
          <h2>What named.network is, and what it is not.</h2>
          <p>The best about page here is a short one.</p>
        </div>
        <div className="honest-grid">
          {cards.map((c, i) => (
            <div key={i} className="honest-card">
              <div className="tag">{c.tag}</div>
              <h4>{c.t}</h4>
              <p>{c.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="section section-dark" style={{ padding: '128px 0' }}>
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <div className="cta-final">
          <span className="section-label">Use the product</span>
          <h2>Start with a private app.<br/><em>Then go deeper if you need to.</em></h2>
          <p>The core flow is the right place to begin. The advanced services are there when the real need shows up.</p>
          <div className="ctas" style={{ marginTop: 36 }}>
            <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Create a workspace <Icon.arrow /></span></a>
            <a href="/docs" className="btn btn-on-dark btn-lg"><span>Read the docs</span></a>
          </div>
        </div>
      </div>
    </section>
  );
}

function App() {
  return (
    <>
      <SiteNav />
      <Hero />
      <Summary />
      <Reality />
      <Boundaries />
      <FinalCTA />
      <SiteFooter />
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

